import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DECIMAL_RE } from "@/lib/format";

const Field = ({ f, value, onChange, prefix }) => {
  const id = `${prefix}-${f.key.replace(/_/g, "-")}`;
  if (f.type === "switch") {
    return (
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{f.label}</Label>
        <Switch id={id} data-testid={`${id}-switch`} checked={!!value} onCheckedChange={onChange} />
      </div>
    );
  }
  if (f.type === "select") {
    return (
      <div className="space-y-2">
        <Label>{f.label}</Label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`${id}-select`}><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>
            {f.options.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`${id}-option-${o.value}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{f.label}</Label>
      <Input id={id} data-testid={`${id}-input`} value={value ?? ""} inputMode={f.type === "decimal" ? "decimal" : undefined}
        className={f.type === "decimal" ? "font-mono" : ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
};

export default function RecordDialog({ open, onOpenChange, title, fields, initial, onSubmit, prefix }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (open) { setForm(initial); setErr(""); }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    const empty = fields.find((f) => f.type === "text" && !f.optional && !String(form[f.key] ?? "").trim());
    if (empty) return setErr(`${empty.label} is required`);
    const bad = fields.find((f) => f.type === "decimal" && !DECIMAL_RE.test(String(form[f.key] ?? "").trim()));
    if (bad) return setErr(`${bad.label} must be a number, e.g. 33.333`);
    setBusy(true);
    const msg = await onSubmit(form);
    setBusy(false);
    if (msg) setErr(msg);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card" data-testid={`${prefix}-dialog`}>
        <DialogHeader><DialogTitle className="font-display tracking-tight">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {fields.map((f) => (
            <Field key={f.key} f={f} prefix={prefix} value={form[f.key]} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} />
          ))}
          {err && <p className="text-sm text-destructive" data-testid={`${prefix}-error`}>{err}</p>}
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid={`${prefix}-save-button`}>{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
